import { GridColDef } from '@mui/x-data-grid'
import { Tooltip, Typography, Chip, Box, IconButton } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import dayjs from 'lib/dayjs'
import './columnDefinitions.scss'

export const createInventoryColumnDefinitions = (
  onEditParValue?: (row: any) => void
): GridColDef[] => [
  {
    field: 'description',
    headerName: 'Product Description',
    width: 400,
    sortable: true,
    renderCell: (params) => {
      return (
        <Tooltip title={params.value}>
          <Typography variant="body2" className="product-description">
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
  },
  {
    field: 'vendorName',
    headerName: 'Manufacturer',
    width: 200,
    sortable: true,
    renderCell: (params) => {
      return (
        <Tooltip title={params.value}>
          <Typography variant="body2" className="manufacturer">
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
  },
  {
    field: 'versionModelNumber',
    headerName: 'Model Number',
    width: 130,
    sortable: true,
    renderCell: (params) => (
      <Typography variant="body2" className="model-number">
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'catalogNumber',
    headerName: 'Catalog Number',
    width: 140,
    sortable: true,
    renderCell: (params) => (
      <Typography variant="body2" className="catalog-number">
        {params.value || '-'}
      </Typography>
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 110,
    sortable: true,
    renderCell: (params) => (
      <Typography variant="body2" className="type-field">
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'disposition',
    headerName: 'Ownership',
    width: 120,
    sortable: true,
    renderCell: (params) => {
      const dispositionMapping = {
        Consigned: 'Consigned',
        Purchased: 'Purchased',
        Mixed: 'Mixed',
      }
      const displayValue =
        dispositionMapping[params.value as keyof typeof dispositionMapping] ||
        params.value
      return (
        <Typography variant="body2" className="ownership-field">
          {displayValue}
        </Typography>
      )
    },
  },
  {
    field: 'parValue',
    headerName: 'PAR Value',
    width: 110,
    sortable: true,
    type: 'number',
    align: 'center',
    renderCell: (params) => {
      return (
        <Box className="par-value-container">
          <Typography variant="body2" className="par-value-text">
            {params.value}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onEditParValue?.(params.row)
            }}
            className="edit-icon-button"
          >
            <EditIcon />
          </IconButton>
        </Box>
      )
    },
  },
  {
    field: 'quantity',
    headerName: 'On Hand',
    width: 90,
    sortable: true,
    type: 'number',
    align: 'center',
    renderCell: (params) => {
      const quantity = params.value
      const parValue = params.row.parValue || 0

      if (quantity >= 0 && quantity <= Math.max(0, parValue - 1)) {
        return <Box className="quantity-low">{quantity}</Box>
      }

      if (quantity >= parValue && quantity <= parValue + 4) {
        return <Box className="quantity-medium">{quantity}</Box>
      }

      return (
        <Typography variant="body2" className="on-hand-text">
          {quantity}
        </Typography>
      )
    },
  },
  {
    field: 'inventoryLocationsNames',
    headerName: 'Locations',
    width: 120,
    sortable: true,
    renderCell: (params) => {
      const locations = params.value as string[]
      if (!locations || locations.length === 0) {
        return (
          <Typography variant="body2" className="location-text">
            -
          </Typography>
        )
      }

      if (locations.length === 1) {
        return (
          <Tooltip title={locations[0]}>
            <Typography variant="body2" className="location-text">
              {locations[0]}
            </Typography>
          </Tooltip>
        )
      }

      return (
        <Tooltip title={locations.join(', ')}>
          <Chip
            label={`${locations.length} locations`}
            size="small"
            variant="outlined"
            className="location-chip"
          />
        </Tooltip>
      )
    },
  },
  {
    field: 'earliestExpirationDate',
    headerName: 'Expiration',
    width: 130,
    sortable: true,
    renderCell: (params) => {
      if (!params.value) {
        return (
          <Typography variant="body2" className="expiration-text">
            -
          </Typography>
        )
      }

      const expirationDate = dayjs(params.value)
      const formattedDate = expirationDate.format('MM/DD/YYYY')

      // Determine CSS class based on expiration status
      let className = 'expiration-normal'
      if (expirationDate.isBefore(dayjs(), 'day')) {
        className = 'expiration-expired' // Expired items in red
      } else if (expirationDate.isBefore(dayjs().add(14, 'months'), 'day')) {
        className = 'expiration-warning' // Items expiring within 14 months in orange
      }

      return (
        <Typography variant="body2" className={className}>
          {formattedDate}
        </Typography>
      )
    },
  },
]
